import React, { useCallback, useMemo } from 'react';
import { BreakpointQueryName, Spacing, SpacingName } from '@doveit/lego';
import { NextPage } from 'next';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import { NEWS_CATEGORIES, PAGINATED_NEWS_QUERY, PAGINATED_NEWS_QUERY_BY_CATEGORY } from '../client/graphql/queries/news';
import { mapArticles } from '../client/graphql/mappers/articleMappers';
import ArticlesList from '../client/components/articles-list/ArticlesList';
import {
  goToPage, buildNewsSerpURLObject,
  buildArticlesURLObject,
  QueryObject,
} from '../client/utils/links';
import { buildPageableSEO } from '../client/utils/seo/seo';
import { getStructuredData, getBreadcrumbStructuredData } from '../data/structured';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import { ArticleType } from '../domain/articleType';
import { AdvFluidType, New, NewsCategory } from '../domain/graphcms';
import { Adv } from '../domain/adv';
import { ArticlesBreadcrumb } from '../client/constants';
import { getAdvByType } from '../services/advs/advs';
import RelatedArticles, { RelatedArticleItem } from '../client/components/related-articles/RelatedArticles';
import AdvFluid from '../client/components/adv-fluid/AdvFluid';
import { BANNER_SELL_PROPERTY } from '../client/themes/icons';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import BottomBarV2 from '../client/templates/common/bottom-bar-v2/BottomBarV2';
import useRefInView from '../client/hooks/useRefInView/useRefInView';
import * as styles from '../client/templates/news-serp/NewsSerp.style';
import { BottomBarData } from '../data/bottom-bars';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { importDynamicData } from '../services/importDynamicData';
import httpErrors from '../client/utils/error/http/httpErrors';
import { ASSET_BY_IDS } from '../client/graphql/queries/asset';
import CategoryList from '../client/components/category-list/CategoryList';

export interface NewsSERPProps {
  SEO: PageHeadProps['SEO'],
  bottomBar: BottomBarData,
  title: string,
  news: New[],
  page: number,
  totalPages: number,
  totalItems: number,
  advSellProperty: Adv,
  advSideSellProperty: Adv,
  promotionalLinks: Omit<RelatedArticleItem, 'onClick'>[],
  category: NewsCategory,
  categories: NewsCategory[],
  query: QueryObject,
}

export const PAGE_SIZE = 30;
const INTERSECTION_OPTIONS = { threshold: 1 };

export const NewsSERP: NextPage<NewsSERPProps> = ({
  SEO,
  news,
  title,
  page,
  totalPages,
  totalItems,
  advSellProperty,
  advSideSellProperty,
  bottomBar,
  promotionalLinks,
  category,
  categories,
  query,
}) => {
  const { ref: headerRef, entry: header } = useRefInView(INTERSECTION_OPTIONS);
  const { ref: relatedArticlesRef, entry: relatedArticles } = useRefInView(INTERSECTION_OPTIONS);

  const breadcrumb = useMemo(() => {
    const items = [{
      label: ArticlesBreadcrumb[ArticleType.NEWS],
      urlObject: buildArticlesURLObject({
        ...query,
        category: undefined,
      }, ArticleType.NEWS),
    }];

    if (category?.slug) {
      items.push({
        label: category.slug,
        urlObject: buildArticlesURLObject(query, ArticleType.NEWS),
      });
    }

    return items;
  }, [query, category]);

  const advInsertion = useCallback((index) => (
    index === 2 && (
      <AdvFluid
        {...advSellProperty}
        symbolPath={BANNER_SELL_PROPERTY}
        dataRef="adv-fluid-v1-main-sell-properties"
        dataTracking="adv-fluid-v1-main-sell-properties"
      />
    )
  ), [advSellProperty]);

  const getPageUrl = useCallback(
    (requestedPage) => buildNewsSerpURLObject({ ...query, p: requestedPage }).as,
    [query],
  );
  const onPageChange = useCallback(
    (requestedPage) => goToPage(buildNewsSerpURLObject({ ...query, p: requestedPage })),
    [query],
  );

  const goToBottomBarLink = useCallback(
    () => goToPage(bottomBar.urlObject),
    [bottomBar],
  );

  return (
    <AppLayout activePage="news">
      <PageHead
        SEO={SEO}
        structuredData={getStructuredData(getBreadcrumbStructuredData(breadcrumb))}
      />
      <PageLayout>
        <PageLayout.Wrapper>
          <PageLayout.Header>
            <div
              ref={headerRef}
              css={styles.header}
            >
              <h1 css={styles.title}>
                {title}
              </h1>
              {categories?.length > 0 && (
                <nav css={styles.categories}>
                  <CategoryList
                    items={[
                      {
                        id: 'tutte-le-news',
                        label: 'Tutte le news',
                        urlObject: buildArticlesURLObject({}, ArticleType.NEWS),
                      },
                      ...categories.map((c) => ({ id: c.slug, label: c.name, urlObject: buildArticlesURLObject({ category: c.slug }, ArticleType.NEWS) }))]}
                    activeId={category ? category.slug : 'tutte-le-news'}
                  />
                </nav>
              )}
            </div>
          </PageLayout.Header>
          <PageLayout.Content>
            <PageLayout.Main>
              <ArticlesList
                articles={mapArticles(news, ArticleType.NEWS)}
                pagination={{
                  totalPages,
                  totalElements: totalItems,
                  pageNumber: page - 1,
                  pageSize: PAGE_SIZE,
                  onPageChange,
                  buildPagePath: getPageUrl,
                }}
                render={{ inPosition: advInsertion }}
              />
            </PageLayout.Main>
            <PageLayout.Aside>
              <AdvFluid
                {...advSideSellProperty}
                symbolPath={BANNER_SELL_PROPERTY}
                dataRef="adv-fluid-v1-aside-sell-property"
                dataTracking="adv-fluid-v1-aside-sell-property"
              />

              <Spacing
                ref={relatedArticlesRef}
                margin={[SpacingName.MEDIUM, 0, 0, 0]}
              >
                <RelatedArticles
                  title="Vedi anche"
                  items={promotionalLinks}
                />
              </Spacing>

              <div
                $visible={relatedArticles?.boundingClientRect.y < 0}
                css={styles.stickySideAdv}
                data-ref="adv-fluid-sticky-aside"
              >
                <AdvFluid
                  {...advSideSellProperty}
                  symbolPath={BANNER_SELL_PROPERTY}
                  dataRef="adv-fluid-v1-sticky-sell-property"
                  dataTracking="adv-fluid-v1-sticky-sell-property"
                />
              </div>
            </PageLayout.Aside>
          </PageLayout.Content>
          <div css={styles.footer}>
            <PageLayout.Breadcrumb items={breadcrumb} />
          </div>
        </PageLayout.Wrapper>

        <BottomBarV2
          hiddenFrom={BreakpointQueryName.LG}
          visible={header?.boundingClientRect.y < 0}
          label={bottomBar.label}
          title={bottomBar.title}
          href={bottomBar.urlObject?.as}
          onClick={goToBottomBarLink}
        />
      </PageLayout>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<NewsSERPProps>(async ({ query }) => {
  const graphQLClient = initGraphQLClient();
  const newsSerpURLObject = buildArticlesURLObject({
    category: query.category,
  }, ArticleType.NEWS);

  const page = Number.parseInt(query.p as string || '1', 10);
  if (Number.isNaN(page) || page < 1) {
    throw new httpErrors.BadRequest(`Invalid page parameter ${page}`);
  }

  const { data: { categories } } = await graphQLClient.query<{ categories: NewsCategory[] }>({
    query: NEWS_CATEGORIES,
  });

  let graphqlQuery: any = {
    query: PAGINATED_NEWS_QUERY,
    variables: {
      skip: PAGE_SIZE * (page - 1),
      first: PAGE_SIZE,
    },
  };

  let category;

  if (query.category) {
    category = categories.find(({ slug }) => slug === query.category);

    if (!category) {
      return {
        redirect: {
          destination: buildArticlesURLObject({}, ArticleType.NEWS).as,
          permanent: false,
        },
      };
    }

    graphqlQuery = {
      query: PAGINATED_NEWS_QUERY_BY_CATEGORY,
      variables: {
        ...graphqlQuery.variables,
        slugCategory: category.slug,
      },
    };
  }

  const [
    { SEO: seoTemplate, bottomBar, promotionalLinks },
    adv,
    { data },
  ] = await Promise.all([
    importDynamicData('pages/news-serp.yml'),
    getAdvByType(AdvFluidType.SELL_PROPERTY),
    graphQLClient.query(graphqlQuery),
  ]);

  const enrichedPromotionalLinks = await graphQLClient.query({
    query: ASSET_BY_IDS,
    variables: {
      ids: promotionalLinks.map((link) => link.thumbnail.handle),
    },
  });

  const promotionalLinksAssetUrls = enrichedPromotionalLinks.data.assets.reduce((acc, asset) => {
    acc[asset.handle] = asset.url;
    return acc;
  }, {});

  const totalItems = data.total.aggregate.count;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  if (page > totalPages) {
    throw new httpErrors.NotFound(`Page parameter ${page} greater than total pages ${totalPages}`);
  }

  const pageable = {
    page,
    first: page === 1,
    last: page === totalPages,
    hasResults: data.news.length > 0,
  };

  return ({
    props: {
      query: {
        ...query,
        category: category?.slug ?? null,
      },
      category: category || null,
      categories,
      title: category?.title || 'Notizie immobiliari e news di Dove.it',
      advSellProperty: adv,
      advSideSellProperty: adv,
      SEO: buildPageableSEO({
        ...(category ? category.seo : seoTemplate),
        urlObject: newsSerpURLObject,
      }, pageable),
      news: data.news,
      page,
      totalPages,
      totalItems,
      bottomBar,
      promotionalLinks: promotionalLinks?.map((link) => ({
        ...link,
        thumbnail: {
          ...link.thumbnail,
          url: promotionalLinksAssetUrls[link.thumbnail.handle],
        },
      })) ?? [],
    },
  });
});

export default withError(NewsSERP);
